/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1712793600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Apr 11 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "trino",
        type: "source",
        icon: null,
        toc: [{
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Data types",
          "depth": 0,
          "id": "data-types"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet, SyncLogsSnippet, NoCommonErrors} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    if (!SyncLogsSnippet) _missingMdxReference("SyncLogsSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in ", _jsx(_components.a, {
          href: "https://trino.io/",
          children: "TrinoDB"
        }), " (previously PrestoSQL) and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to your Trino instance for information like your host, port, database name, and credentials."]
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Trino"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Trino"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Trino coordinator server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Trino coordinator server."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Default catalog"
          }), ": This specifies the catalog that will be used when Hightouch executes queries in Trino."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Engine"
          }), ": Select whether to use the Trino or Presto engine to execute queries."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connector"
          }), ": Select the connector that will be used to access the data source used by the sync engine."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Disable encryption"
          }), ": Encryption is enabled by default. Check this option if your Trino instance doesn't support encrypted connections."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Trino"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Trino"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsx(_components.p, {
        children: "To set up the Lightning engine, you need to grant Hightouch write access to your Trino connector. The steps to do so depend on the connector. These snippets serve as a reference and should be run on your Trino server."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Hive"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_planner;\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "PostgreSQL"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " hightouch_user ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " PASSWORD ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'********'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), ", USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " hightouch_user;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), ", USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " hightouch_user;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The snippet provisions two schemas (", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), ") for storing logs of previously synced data and, in some cases, creates a dedicated SQL user. Hightouch must be able to read and write to these schemas, but the specific username and schema names might vary."]
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": This can be your personal Trino login or a dedicated user for Hightouch. At minimum, this user must have read access to the data you wish to sync. If using the Lightning sync engine, you must also grant this user additional permissions as described above."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Trino"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Trino."]
      }), "\n", _jsx(_components.p, {
        children: "The Trino source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n"]
      }), "\n", _jsx(SyncLogsSnippet, {
        source: "Trino"
      }), "\n", _jsx(_components.h2, {
        id: "data-types",
        children: "Data types"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports all built-in ", _jsx(_components.a, {
          href: "https://trino.io/docs/current/language/types.html",
          children: "Trino data types"
        }), ". Custom data types provided by plugins are represented as strings."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
